/**
 * CreditsWidget — dashboard usage bar + buy-credits modal.
 *
 * Shows "X of Y used" for the current billing period. Flips amber at
 * 80% consumption, red at 95%. "Buy credits" opens a modal with the
 * 4 top-up packs (already registered in backend/services/credits.py's
 * TOPUP_PACKS). Selecting a pack hits the existing
 * `POST /credits/topup/checkout` endpoint and redirects to Stripe.
 *
 * Same backend infrastructure that already existed — this component
 * finally exposes it in the dashboard so the "40 of 50 used" nudge
 * actually converts into revenue.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, Sparkles, X, TrendingUp } from "lucide-react";
import { useCurrency } from "@/lib/currency";

export default function CreditsWidget() {
  const [balance, setBalance] = useState(null);
  const [packs, setPacks] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [busySku, setBusySku] = useState("");
  const { fmt: fx } = useCurrency();

  useEffect(() => {
    let alive = true;
    api.get("/credits/balance").then((r) => alive && setBalance(r.data)).catch(() => {});
    api.get("/credits/topup-packs").then((r) => alive && setPacks(r.data?.packs || [])).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!balance || balance.admin_unlimited) return null;

  const used = balance.monthly_used ?? 0;
  const allowance = balance.monthly_allowance ?? 0;
  const topup = balance.topup_balance ?? 0;
  const pct = allowance > 0 ? Math.min(100, Math.round((used / allowance) * 100)) : 0;
  const remainingMonthly = Math.max(0, allowance - used);
  const totalRemaining = remainingMonthly + topup;

  // Color tone by consumption
  let barColor = "bg-emerald-500";
  let toneLabel = "healthy";
  if (pct >= 95) { barColor = "bg-red-500"; toneLabel = "at cap"; }
  else if (pct >= 80) { barColor = "bg-brand-amber"; toneLabel = "running low"; }
  else if (pct >= 50) { barColor = "bg-cyan-500"; toneLabel = "half used"; }

  const buyPack = async (sku) => {
    setBusySku(sku);
    try {
      const r = await api.post("/credits/topup/checkout", { sku });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("Couldn't start checkout — try again");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed to start");
    } finally {
      setBusySku("");
    }
  };

  return (
    <>
      <div
        data-testid="credits-widget"
        className="rounded-xl border border-line bg-bg-surface p-5 space-y-3"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-brand-amber mb-1">
              // AI CREDITS
            </div>
            <div className="text-ink-primary font-semibold text-sm">
              <span data-testid="credits-widget-used" className="font-mono text-base">{used.toLocaleString()}</span>
              <span className="text-ink-muted"> of </span>
              <span className="font-mono text-base">{allowance.toLocaleString()}</span>
              <span className="text-ink-muted"> used this period</span>
              {topup > 0 && (
                <span className="ml-2 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider">
                  +{topup.toLocaleString()} topped up
                </span>
              )}
            </div>
            <div className="text-ink-muted text-xs mt-0.5 font-mono">
              {totalRemaining.toLocaleString()} left · {toneLabel}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            data-testid="credits-widget-buy-btn"
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-semibold whitespace-nowrap transition ${
              pct >= 80
                ? "popular-pulse bg-brand-amber text-black hover:bg-brand-amber/90"
                : "border border-line bg-bg-elevated text-ink-primary hover:bg-bg-surface"
            }`}
          >
            <Sparkles className="h-3.5 w-3.5" />
            Buy credits
          </button>
        </div>

        {/* Progress bar */}
        <div
          className="h-2 rounded-full bg-bg-elevated overflow-hidden"
          data-testid="credits-widget-progress"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`AI credits used: ${used} of ${allowance}`}
        >
          <div
            className={`${barColor} h-full transition-all duration-500`}
            style={{ width: `${pct}%` }}
          />
        </div>

        {/* Contextual nudge at 80%+ */}
        {pct >= 80 && (
          <div className="flex items-start gap-2 text-xs text-ink-secondary" data-testid="credits-widget-nudge">
            <TrendingUp className="h-3.5 w-3.5 text-brand-amber mt-0.5 shrink-0" />
            <span>
              {pct >= 95 ? (
                <>You&apos;re out of credits. <span className="text-brand-amber font-semibold">Top up now</span> or new generations will fail.</>
              ) : (
                <>Only <span className="text-brand-amber font-semibold">{remainingMonthly} credits</span> left this period. Grab a top-up to keep shipping.</>
              )}
            </span>
          </div>
        )}
      </div>

      {/* Buy credits modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !busySku && setModalOpen(false)}
          data-testid="credits-modal-overlay"
        >
          <div
            className="relative bg-bg-surface border border-line rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="credits-modal"
          >
            <button
              type="button"
              onClick={() => !busySku && setModalOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-md text-ink-muted hover:text-ink-primary hover:bg-bg-elevated transition"
              data-testid="credits-modal-close"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="pr-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-brand-amber mb-1">
                // TOP UP AI CREDITS
              </div>
              <h2 className="font-display text-2xl font-bold text-ink-primary">Buy a credit pack</h2>
              <p className="text-ink-secondary text-sm mt-1">
                Credits stack on top of your monthly allowance and never expire while your account stays active. Applied instantly after checkout.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {packs.map((p) => {
                const perCredit = fx(p.price_usd / p.credits, { decimals: 3 });
                const isBusy = busySku === p.sku;
                const isBest = p.sku === "pack_5000";
                return (
                  <button
                    key={p.sku}
                    type="button"
                    onClick={() => buyPack(p.sku)}
                    disabled={Boolean(busySku)}
                    data-testid={`credits-pack-${p.sku}`}
                    className={`relative text-left rounded-xl border p-4 transition disabled:opacity-60 ${
                      isBest
                        ? "border-brand-amber/60 bg-brand-amber/5 hover:bg-brand-amber/10"
                        : "border-line bg-bg-elevated hover:bg-bg-surface"
                    }`}
                  >
                    {isBest && (
                      <div className="absolute -top-2 left-3 rounded-full bg-brand-amber px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-black font-bold">
                        Best value
                      </div>
                    )}
                    <div className="font-display text-2xl font-black text-ink-primary">
                      {p.credits.toLocaleString()}
                    </div>
                    <div className="text-xs text-ink-muted font-mono uppercase tracking-wider">credits</div>
                    <div className="mt-3 flex items-baseline gap-1">
                      <span className="text-lg font-bold text-ink-primary">{fx(p.price_usd)}</span>
                      <span className="text-[10px] text-ink-muted font-mono">{perCredit}/cr</span>
                    </div>
                    <div className={`mt-3 rounded-md py-1.5 text-center text-xs font-semibold transition ${
                      isBest
                        ? "bg-brand-amber text-black"
                        : "border border-line bg-bg-surface text-ink-primary"
                    }`}>
                      {isBusy ? (
                        <span className="inline-flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Starting…
                        </span>
                      ) : "Buy"}
                    </div>
                  </button>
                );
              })}
            </div>

            <p className="text-[11px] text-ink-muted text-center pt-2">
              Secure checkout by Stripe · Credits appear in your wallet within seconds of payment.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
